import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiArrowRight, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import Button from '@components/common/Button';
import { COMPANY_INFO } from '@utils/constants';

/**
 * HeroSection Component
 * Homepage hero section with image slider and CTA
 */
const HeroSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  // Hero slides data
  const slides = [
    {
      id: 1,
      title: 'Premium White Maize Flour',
      subtitle: 'Quality You Can Trust',
      description: 'UNBS & GMP certified premium white maize flour, ethically sourced from over 1,052 farmers in Eastern Uganda.',
      image: '/whitemaizeflour.png',
      cta: {
        primary: { text: 'Our Products', link: '/products' },
        secondary: { text: 'Learn More', link: '/about' },
      },
    },
    {
      id: 2,
      title: 'Empowering Local Farmers',
      subtitle: 'Community-Driven Excellence',
      description: 'Supporting 10 cooperative societies and over 1,000 farmer members through collective marketing and value addition.',
      image: '/loadingofmaize.jpeg',
      cta: {
        primary: { text: 'Join Us', link: '/membership' },
        secondary: { text: 'Our Cooperatives', link: '/about' },
      },
    },
    {
      id: 3,
      title: 'Leading in Quality',
      subtitle: '20+ Years of Service',
      description: 'Committed to consistency, traceability, and ethical sourcing. Your trusted partner for quality maize products.',
      image: '/loadingofmaize.jpeg',
      cta: {
        primary: { text: 'Get Quote', link: '/contact' },
        secondary: { text: 'Our Services', link: '/services' },
      },
    },
  ];

  // Auto-advance slides
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 7000); // Change slide every 7 seconds

    return () => clearInterval(timer);
  }, [slides.length]);

  // Navigation functions
  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  return (
    <section className="relative h-[600px] md:h-[700px] lg:h-[800px] overflow-hidden">
      {/* Slides */}
      {slides.map((slide, index) => (
        <div
          key={slide.id}
          className={`
            absolute inset-0 transition-all duration-1000 ease-in-out
            ${
              index === currentSlide
                ? 'opacity-100 translate-x-0'
                : index < currentSlide
                ? 'opacity-0 -translate-x-full'
                : 'opacity-0 translate-x-full'
            }
          `}
        >
          {/* Background Image */}
          <div className="absolute inset-0">
            <img
              src={slide.image}
              alt={slide.title}
              className="w-full h-full object-cover"
            />
            {/* Overlay */}
            <div className="absolute inset-0 bg-gradient-to-r from-bukawa-primary-900/95 via-bukawa-primary-800/85 to-bukawa-primary-900/70" />
          </div>

          {/* Content */}
          <div className="relative h-full container-custom">
            <div className="flex items-center h-full">
              <div className="max-w-3xl">
                {/* Subtitle */}
                <div className="inline-flex items-center gap-2 mb-4 animate-fade-in-down">
                  <span className="h-px w-12 bg-bukawa-secondary" />
                  <span className="text-bukawa-secondary font-medium text-sm md:text-base uppercase tracking-wider">
                    {slide.subtitle}
                  </span>
                </div>

                {/* Title */}
                <h1 className="text-4xl md:text-5xl lg:text-7xl font-heading font-bold text-white mb-6 leading-tight animate-fade-in-up">
                  {slide.title}
                </h1>

                {/* Description */}
                <p className="text-lg md:text-xl text-white/90 mb-8 leading-relaxed max-w-2xl animate-fade-in-up animation-delay-200">
                  {slide.description}
                </p>

                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up animation-delay-400">
                  <Button
                    to={slide.cta.primary.link}
                    variant="secondary"
                    size="lg"
                    icon={<FiArrowRight />}
                    iconPosition="right"
                  >
                    {slide.cta.primary.text}
                  </Button>
                  <Button
                    to={slide.cta.secondary.link}
                    variant="outline"
                    size="lg"
                    className="border-white text-white hover:bg-white hover:text-bukawa-primary"
                  >
                    {slide.cta.secondary.text}
                  </Button>
                </div>

                {/* Stats - Only on first slide */}
                {index === 0 && (
                  <div className="grid grid-cols-3 gap-6 mt-12 animate-fade-in-up animation-delay-600">
                    <div>
                      <div className="text-3xl md:text-4xl font-bold text-bukawa-secondary mb-1">
                        {COMPANY_INFO.totalMembers}+
                      </div>
                      <div className="text-sm md:text-base text-white/80">
                        Farmer Members
                      </div>
                    </div>
                    <div>
                      <div className="text-3xl md:text-4xl font-bold text-bukawa-secondary mb-1">
                        {COMPANY_INFO.totalCooperatives}
                      </div>
                      <div className="text-sm md:text-base text-white/80">
                        Cooperatives
                      </div>
                    </div>
                    <div>
                      <div className="text-3xl md:text-4xl font-bold text-bukawa-secondary mb-1">
                        20+
                      </div>
                      <div className="text-sm md:text-base text-white/80">
                        Years Service
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Navigation Arrows */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="container-custom h-full flex items-center justify-between">
          {/* Previous Button */}
          <button
            onClick={prevSlide}
            className="pointer-events-auto p-3 rounded-full bg-white/10 backdrop-blur-sm text-white hover:bg-white/20 transition-all"
            aria-label="Previous slide"
          >
            <FiChevronLeft className="h-6 w-6" />
          </button>

          {/* Next Button */}
          <button
            onClick={nextSlide}
            className="pointer-events-auto p-3 rounded-full bg-white/10 backdrop-blur-sm text-white hover:bg-white/20 transition-all"
            aria-label="Next slide"
          >
            <FiChevronRight className="h-6 w-6" />
          </button>
        </div>
      </div>

      {/* Slide Indicators */}
      <div className="absolute bottom-8 left-0 right-0 z-10">
        <div className="container-custom">
          <div className="flex items-center gap-3">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`
                  h-1 rounded-full transition-all duration-300
                  ${
                    index === currentSlide
                      ? 'bg-bukawa-secondary w-12'
                      : 'bg-white/50 w-8 hover:bg-white/70'
                  }
                `}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 right-8 animate-bounce">
        <div className="flex flex-col items-center gap-2 text-white/60">
          <span className="text-xs uppercase tracking-wider">Scroll</span>
          <svg
            className="h-6 w-6"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;